import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

interface ApiResponse<T = any> {
  status: 'success' | 'error';
  message?: string;
  data?: T;
  errors?: string[];
}

class ApiService {
  private baseURL: string;
  private adminToken: string | null = null;
  private clientToken: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.adminToken = localStorage.getItem('adminToken');
    this.clientToken = localStorage.getItem('clientToken');
  }

  private async request<T>(
    endpoint: string,
    method: string = 'GET',
    body?: any,
    useClientToken: boolean = false
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    
    const token = useClientToken ? this.clientToken : this.adminToken;
    
    const config: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      ...(body && { body: JSON.stringify(body) }),
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        console.log('API Error Response:', { status: response.status, data });
        // Handle validation errors specifically
        if (data.errors && Array.isArray(data.errors)) {
          const errorMessages = data.errors.map((err: any) => err.msg || err.message).join(', ');
          throw new Error(`Validation failed: ${errorMessages}`);
        }
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<ApiResponse<{ user: any; token: string }>> {
    const response = await this.request<{ user: any; token: string }>('/auth/login', 'POST', { email, password });

    if (response.status === 'success' && response.data?.token) {
      this.adminToken = response.data.token;
      localStorage.setItem('adminToken', response.data.token);
    }

    return response;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.request('/auth/logout', 'POST');

    this.adminToken = null;
    localStorage.removeItem('adminToken');
    return response;
  }

  async getMe(): Promise<ApiResponse<{ user: any }>> {
    return this.request<{ user: any }>('/auth/me');
  }

  // Shop endpoints
  async getShops(params?: {
    page?: number;
    limit?: number;
    purpose?: string;
    floor?: string;
    status?: string;
    minSize?: number;
    maxSize?: number;
    search?: string;
  }): Promise<ApiResponse<{ shops: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/shops?${queryString}` : '/shops';
    
    return this.request<{ shops: any[]; pagination: any }>(endpoint);
  }

  async getShop(id: string): Promise<ApiResponse<{ shop: any }>> {
    return this.request<{ shop: any }>(`/shops/${id}`);
  }

  async getAvailableShops(): Promise<ApiResponse<{ shops: any[] }>> {
    return this.request<{ shops: any[] }>('/shops/available');
  }

  async createShop(shopData: any): Promise<ApiResponse<{ shop: any }>> {
    return this.request<{ shop: any }>('/shops', 'POST', shopData);
  }

  async updateShop(id: string, shopData: any): Promise<ApiResponse<{ shop: any }>> {
    return this.request<{ shop: any }>(`/shops/${id}`, 'PUT', shopData);
  }

  async deleteShop(id: string): Promise<ApiResponse> {
    return this.request(`/shops/${id}`, 'DELETE');
  }

  async updateShopStatus(id: string, status: string): Promise<ApiResponse<{ shop: any }>> {
    return this.request<{ shop: any }>(`/shops/${id}/status`, 'PUT', { status });
  }

  async searchShops(query: string, params?: { page?: number; limit?: number }): Promise<ApiResponse<{ shops: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    return this.request<{ shops: any[]; pagination: any }>(`/shops/search?${queryParams.toString()}`);
  }

  // Application endpoints
  async getApplications(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<ApiResponse<{ applications: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/applications?${queryString}` : '/applications';
    
    return this.request<{ applications: any[]; pagination: any }>(endpoint);
  }

  async getApplication(id: string): Promise<ApiResponse<{ application: any }>> {
    return this.request<{ application: any }>(`/applications/${id}`);
  }

  async createApplication(applicationData: any): Promise<ApiResponse<{ application: any }>> {
    return this.request<{ application: any }>('/applications', 'POST', applicationData);
  }

  async updateApplicationStatus(id: string, status: string, notes?: string): Promise<ApiResponse<{ application: any }>> {
    const requestBody = { status, notes };
    console.log('Sending status update request:', { id, requestBody });
    return this.request<{ application: any }>(`/applications/${id}/status`, 'PUT', requestBody);
  }

  async deleteApplication(id: string): Promise<ApiResponse> {
    return this.request(`/applications/${id}`, 'DELETE');
  }

  async searchApplications(query: string, params?: { page?: number; limit?: number }): Promise<ApiResponse<{ applications: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    queryParams.append('q', query);
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    return this.request<{ applications: any[]; pagination: any }>(`/applications/search?${queryParams.toString()}`);
  }

  async exportApplications(): Promise<Blob> {
    const response = await fetch(`${this.baseURL}/applications/export`, {
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  // Lease Application methods
  async getLeaseApplications(params?: {
    page?: number;
    limit?: number;
    status?: string;
    companyName?: string;
  }): Promise<ApiResponse<{ applications: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.companyName) queryParams.append('companyName', params.companyName);

    const url = `/lease-applications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.request(url, 'GET');
  }

  async updateLeaseApplicationStatus(id: string, status: string, notes?: string): Promise<ApiResponse<{ application: any }>> {
    console.log('Sending lease application status update request:', { id, requestBody: { status, notes } });
    return this.request(`/lease-applications/${id}/status`, 'PUT', { status, notes });
  }

  async createLeaseApplication(applicationData: any): Promise<ApiResponse<{ application: any }>> {
    console.log('Creating lease application:', applicationData);
    return this.request('/lease-applications', 'POST', { CommercialLeasingInterestForm: applicationData });
  }

  async deleteLeaseApplication(id: string): Promise<ApiResponse> {
    return this.request(`/lease-applications/${id}`, 'DELETE');
  }

  async exportLeaseApplications(): Promise<Blob> {
    const response = await fetch(`${this.baseURL}/lease-applications/export`, {
      headers: {
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
      },
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.blob();
  }

  // Stats endpoints
  async getHomeStats(): Promise<ApiResponse<{
    availableSpaces: number;
    floorLevels: number;
    occupancyRate: string;
    rating: number;
  }>> {
    return this.request('/stats/home');
  }

  // Admin endpoints
  async getDashboardStats(): Promise<ApiResponse<{
    totalShops: number;
    availableShops: number;
    pendingApplications: number;
    monthlyRevenue: number;
    occupancyRate: number;
  }>> {
    return this.request('/admin/dashboard');
  }

  async getApplicationStatistics(): Promise<ApiResponse<{
    total: number;
    submitted: number;
    underReview: number;
    approved: number;
    rejected: number;
  }>> {
    return this.request('/applications/statistics');
  }

  async getShopStatistics(): Promise<ApiResponse<{
    total: number;
    available: number;
    rented: number;
    reserved: number;
    unavailable: number;
  }>> {
    return this.request('/shops/statistics/overview');
  }

  // Tenant endpoints
  async getTenants(params?: {
    page?: number;
    limit?: number;
    status?: string;
    shop?: string;
  }): Promise<ApiResponse<{ tenants: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/tenants?${queryString}` : '/tenants';
    
    return this.request<{ tenants: any[]; pagination: any }>(endpoint);
  }

  async getTenant(id: string): Promise<ApiResponse<{ tenant: any }>> {
    return this.request<{ tenant: any }>(`/tenants/${id}`);
  }

  async createTenantFromApplication(applicationId: string, monthlyRent: number, notes?: string): Promise<ApiResponse<{ tenant: any }>> {
    return this.request<{ tenant: any }>('/tenants/from-application', 'POST', { applicationId, monthlyRent, notes });
  }

  async createTenantFromLeaseApplication(applicationId: string, monthlyRent: number, notes?: string): Promise<ApiResponse<{ tenant: any }>> {
    return this.request<{ tenant: any }>(`/lease-applications/${applicationId}/create-tenant`, 'POST', { monthlyRent, notes });
  }

  async createTenant(tenantData: any): Promise<ApiResponse<{ tenant: any }>> {
    return this.request<{ tenant: any }>('/tenants', 'POST', tenantData);
  }

  async updateTenant(id: string, tenantData: any): Promise<ApiResponse<{ tenant: any }>> {
    return this.request<{ tenant: any }>(`/tenants/${id}`, 'PUT', tenantData);
  }

  async transferTenant(id: string, newShopId: string, monthlyRent: number, reason?: string, notes?: string): Promise<ApiResponse<{ tenant: any }>> {
    return this.request<{ tenant: any }>(`/tenants/${id}/transfer`, 'PUT', { newShopId, monthlyRent, reason, notes });
  }

  async evictTenant(id: string, reason: string, reasonDetails?: string, notes?: string): Promise<ApiResponse<{ tenant: any }>> {
    return this.request<{ tenant: any }>(`/tenants/${id}/evict`, 'PUT', { reason, reasonDetails, notes });
  }

  async getTenantHistory(id: string): Promise<ApiResponse<{ history: any[] }>> {
    return this.request<{ history: any[] }>(`/tenants/${id}/history`);
  }

  async getTenantPayments(id: string): Promise<ApiResponse<{ payments: any[] }>> {
    return this.request<{ payments: any[] }>(`/tenants/${id}/payments`);
  }

  async addTenantPayment(id: string, payment: {
    amount: number;
    paidAt?: string;
    period?: string;
    method?: 'cash' | 'bank_transfer' | 'mobile_money' | 'card' | 'other';
    reference?: string;
    notes?: string;
  }): Promise<ApiResponse<{ tenantId: string; payments: any[] }>> {
    return this.request<{ tenantId: string; payments: any[] }>(`/tenants/${id}/payments`, 'POST', payment);
  }

  async updateTenantPayment(id: string, paymentId: string, payment: {
    amount?: number;
    paidAt?: string;
    period?: string;
    method?: 'cash' | 'bank_transfer' | 'mobile_money' | 'card' | 'other';
    reference?: string;
    notes?: string;
  }): Promise<ApiResponse<{ tenantId: string; payments: any[] }>> {
    return this.request<{ tenantId: string; payments: any[] }>(`/tenants/${id}/payments/${paymentId}`, 'PUT', payment);
  }

  async deleteTenantPayment(id: string, paymentId: string): Promise<ApiResponse<{ tenantId: string; payments: any[] }>> {
    return this.request<{ tenantId: string; payments: any[] }>(`/tenants/${id}/payments/${paymentId}`, 'DELETE');
  }

  async getTenantStatistics(): Promise<ApiResponse<{ statistics: any }>> {
    return this.request<{ statistics: any }>('/tenants/statistics');
  }

  // Settings endpoints
  async getSettings(): Promise<ApiResponse<{
    system: any;
    business: any;
    lease: any;
    notifications: any;
  }>> {
    return this.request('/admin/settings');
  }

  async updateSettings(settings: any): Promise<ApiResponse> {
    return this.request('/admin/settings', 'PUT', settings);
  }

  // Public settings endpoint (no auth required)
  async getPublicSettings(): Promise<ApiResponse<{
    business: {
      name: string;
      address: string;
      phone: string;
      email: string;
      description: string;
      businessHours: string;
    };
  }>> {
    return this.request('/settings/public');
  }

  // Health check
  async healthCheck(): Promise<ApiResponse<{
    status: string;
    message: string;
    timestamp: string;
    environment: string;
  }>> {
    return this.request('/health');
  }

  // Recent activity endpoint
  async getRecentActivity(params?: {
    limit?: number;
  }): Promise<ApiResponse<{
    activities: any[];
    total: number;
  }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/admin/recent-activity?${queryString}` : '/admin/recent-activity';
    
    return this.request<{ activities: any[]; total: number }>(endpoint);
  }

  // Client endpoints
  async clientLogin(name: string, email: string): Promise<ApiResponse<{ client: any; token: string }>> {
    const response = await this.request<{ client: any; token: string }>('/client/login', 'POST', { name, email });
    
    if (response.status === 'success' && response.data?.token) {
      this.clientToken = response.data.token;
      localStorage.setItem('clientToken', response.data.token);
    }
    
    return response;
  }

  async getClientDashboard(): Promise<ApiResponse<{ client: any; applications: any[]; leaseApplications: any[]; unreadCounts: any; recentMessages: any[]; stats: any }>> {
    return this.request<{ client: any; applications: any[]; leaseApplications: any[]; unreadCounts: any; recentMessages: any[]; stats: any }>('/client/dashboard', 'GET', undefined, true);
  }

  async getClientApplications(): Promise<ApiResponse<{ applications: any[]; leaseApplications: any[] }>> {
    return this.request<{ applications: any[]; leaseApplications: any[] }>('/client/applications', 'GET', undefined, true);
  }

  async getClientApplication(id: string): Promise<ApiResponse<{ application: any }>> {
    return this.request<{ application: any }>(`/client/applications/${id}`, 'GET', undefined, true);
  }

  async uploadApplicationImages(id: string, images: string[]): Promise<ApiResponse<{ application: any }>> {
    return this.request<{ application: any }>(`/client/applications/${id}/images`, 'POST', { images }, true);
  }

  async getApplicationMessages(id: string): Promise<ApiResponse<{ thread: any; messages: any[] }>> {
    return this.request<{ thread: any; messages: any[] }>(`/client/applications/${id}/messages`, 'GET', undefined, true);
  }

  async sendMessage(id: string, content: string, attachments?: any[]): Promise<ApiResponse<{ message: any }>> {
    return this.request<{ message: any }>(`/client/applications/${id}/messages`, 'POST', { content, attachments }, true);
  }

  // Admin messaging endpoints
  async getMessageThreads(params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<ApiResponse<{ threads: any[]; pagination: any }>> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/admin/messages/threads?${queryString}` : '/admin/messages/threads';
    
    return this.request<{ threads: any[]; pagination: any }>(endpoint);
  }

  async getThreadMessages(threadId: string): Promise<ApiResponse<{ thread: any; messages: any[] }>> {
    return this.request<{ thread: any; messages: any[] }>(`/admin/messages/threads/${threadId}`);
  }

  async sendMessageToApplicant(threadId: string, content: string, attachments?: any[]): Promise<ApiResponse<{ message: any }>> {
    return this.request<{ message: any }>(`/admin/messages/threads/${threadId}`, 'POST', { content, attachments });
  }

  async assignAdminToThread(threadId: string): Promise<ApiResponse<{ thread: any }>> {
    return this.request<{ thread: any }>(`/admin/messages/threads/${threadId}/assign`, 'PUT');
  }

  async updateThreadStatus(threadId: string, status: string): Promise<ApiResponse<{ thread: any }>> {
    return this.request<{ thread: any }>(`/admin/messages/threads/${threadId}/status`, 'PUT', { status });
  }

  async getUnreadMessageCount(): Promise<ApiResponse<{ unreadCount: number }>> {
    return this.request<{ unreadCount: number }>('/admin/messages/unread-count');
  }

  async getMessageStatistics(): Promise<ApiResponse<{ threads: any; messages: any; recentMessages: any[] }>> {
    return this.request<{ threads: any; messages: any; recentMessages: any[] }>('/admin/messages/statistics');
  }

  async startConversation(applicationId: string, initialMessage?: string): Promise<ApiResponse<{ thread: any }>> {
    return this.request<{ thread: any }>('/admin/messages/start-conversation', 'POST', { applicationId, initialMessage });
  }
}

export const apiService = new ApiService(API_BASE_URL);
export default apiService;
